import { deriveCanonicalEvidenceMetrics } from '@canonical-process';
import { normalizeRule, RULE_STATUS, RULE_TYPES } from './ruleSchema';

function compare(actual, operator, threshold) {
  if (!Number.isFinite(actual) || !Number.isFinite(threshold)) return null;
  if (operator === '>') return actual > threshold;
  if (operator === '>=') return actual >= threshold;
  if (operator === '<') return actual < threshold;
  if (operator === '<=') return actual <= threshold;
  if (operator === '==') return actual === threshold;
  if (operator === '!=') return actual !== threshold;
  return null;
}

function getAttrValue(record, attribute) {
  if (!record || !attribute) return undefined;
  const target = String(attribute).toLowerCase();
  const direct = record?.attributes || {};
  for (const [key, value] of Object.entries(direct)) {
    if (String(key).toLowerCase() === target || String(key).toLowerCase().includes(target) || target.includes(String(key).toLowerCase())) return value;
  }
  for (const [key, value] of Object.entries(record)) {
    if (String(key).toLowerCase() === target) return value;
  }
  return undefined;
}

function hasValue(value) {
  return !(value === undefined || value === null || String(value).trim() === '');
}

function formatValue(value, unit) {
  if (!Number.isFinite(value)) return 'Not available';
  if (unit === 'percent') return `${(value * 100).toFixed(value < 0.1 ? 1 : 0)}%`;
  if (unit === 'ratio') return value.toFixed(2);
  if (unit === 'seconds') {
    const hours = value / 3600;
    return hours >= 24 ? `${(hours / 24).toFixed(1)}d` : `${hours.toFixed(1)}h`;
  }
  return Number.isInteger(value) ? value.toLocaleString() : value.toFixed(1);
}

export function evaluateDeterministicRule(inputRule, dataset, providedMetrics = null) {
  const rule = normalizeRule(inputRule);
  const metrics = providedMetrics || deriveCanonicalEvidenceMetrics(dataset || {});
  const cases = Array.isArray(dataset?.cases) ? dataset.cases : [];
  const events = Array.isArray(dataset?.events) ? dataset.events : [];

  if (rule.status === RULE_STATUS.DISABLED) {
    return { ruleId: rule.id, outcome: 'disabled', evaluated: false, affectedCases: 0, affectedPct: 0, evidence: 'Rule disabled', observedValue: null, observedDisplay: 'Disabled' };
  }
  if (rule.status === RULE_STATUS.SUGGESTED) {
    return { ruleId: rule.id, outcome: 'suggested', evaluated: false, affectedCases: 0, affectedPct: 0, evidence: rule.evidenceBasis || 'AI suggestion awaiting review', observedValue: null, observedDisplay: 'Suggested', severity: rule.severity };
  }

  let observedValue = null;
  let affectedCases = 0;
  let affectedPct = 0;
  let issue = null;
  let evidence = '';

  if (rule.ruleType === RULE_TYPES.METRIC_THRESHOLD) {
    observedValue = Number(metrics?.[rule.metric]);
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${rule.metric || 'Metric'} is ${formatValue(observedValue, rule.unit)}.`;
  } else if (rule.ruleType === RULE_TYPES.METRIC_RATIO_THRESHOLD) {
    const numerator = Number(metrics?.[rule.numeratorMetric]);
    const denominator = Number(metrics?.[rule.denominatorMetric]);
    observedValue = Number.isFinite(numerator) && Number.isFinite(denominator) && denominator !== 0 ? numerator / denominator : NaN;
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${rule.numeratorMetric} / ${rule.denominatorMetric} is ${formatValue(observedValue, rule.unit)}.`;
  } else if (rule.ruleType === RULE_TYPES.ATTRIBUTE_COMPLETENESS) {
    if (!cases.length) observedValue = NaN;
    else {
      const complete = cases.filter(c => hasValue(getAttrValue(c, rule.attribute))).length;
      observedValue = complete / cases.length;
      affectedCases = cases.length - complete;
      affectedPct = cases.length ? affectedCases / cases.length : 0;
    }
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${formatValue(observedValue, 'percent')} populated; ${affectedCases.toLocaleString()} missing.`;
  } else if (rule.ruleType === RULE_TYPES.REQUIRED_ACTIVITY) {
    const grouped = new Map();
    events.forEach(e => {
      const id = String(e?.caseId ?? e?.case_id ?? '');
      if (!id) return;
      if (!grouped.has(id)) grouped.set(id, []);
      grouped.get(id).push(e);
    });
    const activity = String(rule.activity || '').toLowerCase();
    let failures = 0;
    for (const caseEvents of grouped.values()) {
      const found = caseEvents.some(e => String(e?.activity || '').toLowerCase() === activity);
      if (!found) failures += 1;
    }
    affectedCases = failures;
    affectedPct = grouped.size ? failures / grouped.size : 0;
    observedValue = grouped.size ? 1 - affectedPct : NaN;
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${formatValue(observedValue, 'percent')} of cases include “${rule.activity}”; ${failures.toLocaleString()} do not.`;
  } else if (rule.ruleType === RULE_TYPES.REQUIRED_SEQUENCE) {
    const grouped = new Map();
    events.forEach(e => {
      const id = String(e?.caseId ?? e?.case_id ?? '');
      if (!id) return;
      if (!grouped.has(id)) grouped.set(id, []);
      grouped.get(id).push(e);
    });
    let failures = 0;
    for (const caseEvents of grouped.values()) {
      const ordered = [...caseEvents].sort((a,b) => String(a?.timestamp || '').localeCompare(String(b?.timestamp || '')));
      const from = ordered.findIndex(e => String(e?.activity || '').toLowerCase() === String(rule.fromActivity || '').toLowerCase());
      const to = ordered.findIndex(e => String(e?.activity || '').toLowerCase() === String(rule.toActivity || '').toLowerCase());
      if (from < 0 || to < 0 || from >= to) failures += 1;
    }
    affectedCases = failures;
    affectedPct = grouped.size ? failures / grouped.size : 0;
    observedValue = grouped.size ? 1 - affectedPct : NaN;
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${formatValue(observedValue, 'percent')} follow “${rule.fromActivity}” before “${rule.toActivity}”; ${failures.toLocaleString()} do not.`;
  } else if (rule.ruleType === RULE_TYPES.FORBIDDEN_TRANSITION) {
    const grouped = new Map();
    events.forEach(e => {
      const id = String(e?.caseId ?? e?.case_id ?? '');
      if (!id) return;
      if (!grouped.has(id)) grouped.set(id, []);
      grouped.get(id).push(e);
    });
    const fromTarget = String(rule.fromActivity || '').toLowerCase();
    const toTarget = String(rule.toActivity || '').toLowerCase();
    let failures = 0;
    for (const caseEvents of grouped.values()) {
      const ordered = [...caseEvents].sort((a,b) => String(a?.timestamp || '').localeCompare(String(b?.timestamp || '')));
      let found = false;
      for (let i = 0; i < ordered.length - 1; i += 1) {
        const from = String(ordered[i]?.activity || '').toLowerCase();
        const to = String(ordered[i + 1]?.activity || '').toLowerCase();
        if (from === fromTarget && to === toTarget) { found = true; break; }
      }
      if (found) failures += 1;
    }
    affectedCases = failures;
    affectedPct = grouped.size ? failures / grouped.size : 0;
    observedValue = grouped.size ? affectedPct : NaN;
    issue = compare(observedValue, rule.operator, Number(rule.threshold));
    evidence = `${formatValue(observedValue, 'percent')} of cases contain the forbidden transition “${rule.fromActivity} → ${rule.toActivity}” (${failures.toLocaleString()} cases).`;
  }

  const evaluated = Number.isFinite(observedValue) && issue !== null;
  const thresholdDisplay = `${rule.operator} ${formatValue(Number(rule.threshold), rule.unit)}`;
  return {
    ruleId: rule.id,
    outcome: !evaluated ? 'not_evaluated' : issue ? 'attention' : 'pass',
    evaluated,
    affectedCases,
    affectedPct,
    observedValue: Number.isFinite(observedValue) ? observedValue : null,
    observedDisplay: formatValue(observedValue, rule.unit),
    thresholdDisplay,
    evidence: evaluated ? evidence : 'The required data is not available in this model.',
    severity: rule.severity,
  };
}

export function evaluateDeterministicRules(definitions = [], dataset) {
  const metrics = deriveCanonicalEvidenceMetrics(dataset || {});
  return definitions.map(rule => ({ ...evaluateDeterministicRule(rule, dataset, metrics), evaluatedAt: new Date().toISOString() }));
}
